import styled from "styled-components"

import { UITypes } from "types"

import WidgetsLayout from "layouts/WidgetsLayout"
import { RainbowChart } from "components/common/RainbowChart"
import { Spinner } from "components/common/Spinner"

type ChartOptions = {
  cutout?: string
  rotation?: number
  circumference?: number
}

const StyledWidgetLayout = styled(WidgetsLayout)`
  width: initial;
  max-width: initial;
  flex: 1 0 300px;
`

const WidgetContent = styled.div<{ $disabled?: boolean }>`
  position: relative;
  margin-top: 20px !important;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "auto")};
`

const ChartWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50px;
  position: relative;
`

const Chart = styled.div`
  position: absolute;
  bottom: -126px;
`

const Text = styled.span`
  font-size: 12px;
  color: #64748b;
  text-transform: uppercase;
`

const WidgetMainValue = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 16px 0 10px;
`

const WidgetValueWrapper = styled.div<{ $inline?: boolean }>`
  margin-top: ${({ $inline }) => ($inline ? "0" : "4px")};
  font-size: ${({ $inline }) => ($inline ? "14px" : "24px")};
  font-weight: 500;
  line-height: 1.5;
  color: ${({ $inline }) => ($inline ? "#0f172a" : "inherit")};
`

const ValueWithPercentage = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 8px;

  .percentage {
    font-size: 18px;
    font-weight: 300;
    color: #475569;
  }
`

const WidgetFooter = styled.footer<{ $wrap?: boolean; $centeredWrap?: boolean }>`
  border-top: 1px solid #f1f5f9;
  padding-top: 16px;
  display: flex;
  flex-wrap: ${({ $wrap, $centeredWrap }) => ($wrap || $centeredWrap ? "wrap" : "nowrap")};
  justify-content: center;
  gap: ${({ $wrap, $centeredWrap }) => ($centeredWrap ? "4px 24px" : $wrap ? "8px 16px" : "0")};
`

const WidgetFooterItem = styled.div<{
  $wrap?: boolean
  $columns?: number
  $inline?: boolean
  $centeredWrap?: boolean
}>`
  text-align: ${({ $centeredWrap }) => ($centeredWrap ? "left" : "center")};
  border-right: ${({ $wrap, $centeredWrap }) => ($wrap || $centeredWrap ? "none" : "1px solid #f1f5f9")};
  flex: ${({ $wrap, $columns, $centeredWrap }) =>
    $centeredWrap ? "0 0 auto" : $wrap ? `1 1 calc(${100 / ($columns || 3)}% - 12px)` : "1"};
  min-width: ${({ $wrap, $centeredWrap }) => ($wrap && !$centeredWrap ? "80px" : $centeredWrap ? "0" : "0")};
  display: flex;
  flex-direction: ${({ $inline, $centeredWrap }) => ($inline || $centeredWrap ? "row" : "column")};
  align-items: center;
  justify-content: ${({ $centeredWrap }) => ($centeredWrap ? "flex-start" : "center")};
  gap: ${({ $inline, $centeredWrap }) => ($inline || $centeredWrap ? "4px" : "0")};

  &:last-child {
    border: none;
  }
`

const WidgetNote = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  color: #64748b;

  &::before {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    display: inline-flex;
    margin-right: 4px;
    content: "";
    background-color: ${({ $color }) => $color};
  }
`

const WidgetLoader = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
`

export interface HalfDoughnutSummaryItem {
  label: string
  color: string
  value: number
}

interface HalfDoughnutSummaryWidgetProps {
  title: string
  mainLabel: string
  items: HalfDoughnutSummaryItem[]
  chartOptions?: ChartOptions
  isFetching?: boolean
  showPercentage?: boolean
  legendWrap?: boolean
  legendInline?: boolean
  legendCenteredWrap?: boolean
  dateSelectOptions?: UITypes.Option[]
  defaultSelectedDateOption?: UITypes.Option
  onSelectDateRange?: (date: [string, string]) => void
  minDate?: string
}

const formatPercentage = (value: number, total: number): string => {
  if (total === 0) return "(0%)"
  const pct = (value / total) * 100
  return pct % 1 === 0 ? `(${pct}%)` : `(${pct.toFixed(1)}%)`
}

export const HalfDoughnutSummaryWidget = ({
  title,
  mainLabel,
  items,
  chartOptions,
  isFetching = false,
  showPercentage = false,
  legendWrap = false,
  legendInline = false,
  legendCenteredWrap = false,
  dateSelectOptions,
  defaultSelectedDateOption,
  onSelectDateRange,
  minDate,
}: HalfDoughnutSummaryWidgetProps) => {
  const total = items.reduce((sum, item) => sum + item.value, 0)
  const colors = items.map(item => item.color)
  const values = items.map(item => item.value)
  const legendColumns = legendWrap && legendInline && !legendCenteredWrap ? 4 : 3
  const useInlineStyle = legendInline || legendCenteredWrap

  return (
    <StyledWidgetLayout
      title={title}
      minDate={minDate}
      onSelectDateRange={onSelectDateRange}
      dateSelectOptions={dateSelectOptions}
      defaultSelectedDateOption={defaultSelectedDateOption}
    >
      <WidgetContent $disabled={isFetching}>
        <ChartWrapper>
          <Chart>
            <RainbowChart colors={colors} values={values} options={chartOptions} />
          </Chart>
        </ChartWrapper>
        <WidgetMainValue>
          <Text>{mainLabel}</Text>
          <WidgetValueWrapper>{total.toLocaleString()}</WidgetValueWrapper>
        </WidgetMainValue>
        <WidgetFooter $wrap={legendWrap} $centeredWrap={legendCenteredWrap}>
          {items.map(item => (
            <WidgetFooterItem
              key={item.label}
              $wrap={legendWrap}
              $columns={legendColumns}
              $inline={useInlineStyle}
              $centeredWrap={legendCenteredWrap}
            >
              <WidgetNote $color={item.color}>{item.label}</WidgetNote>
              <WidgetValueWrapper $inline={useInlineStyle}>
                {showPercentage ? (
                  <ValueWithPercentage>
                    {item.value.toLocaleString()}
                    <span className="percentage">{formatPercentage(item.value, total)}</span>
                  </ValueWithPercentage>
                ) : (
                  item.value.toLocaleString()
                )}
              </WidgetValueWrapper>
            </WidgetFooterItem>
          ))}
        </WidgetFooter>
        {isFetching && (
          <WidgetLoader>
            <Spinner />
          </WidgetLoader>
        )}
      </WidgetContent>
    </StyledWidgetLayout>
  )
}
